import React, { useState, useCallback, useEffect } from 'react';
import { X, FileSignature } from 'lucide-react';
import TemplateEditor from './template-editor/TemplateEditor';
import { signingService } from '../../services/api';

const TemplateEditorModal = ({
  isOpen,
  onClose,
  onSuccess,
  templateId = null, // For edit mode
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [initialTemplate, setInitialTemplate] = useState(null);
  const [error, setError] = useState(null);

  const isEditMode = !!templateId;

  // Load existing template data when in edit mode
  useEffect(() => {
    if (isOpen && templateId) {
      loadTemplate(templateId);
    } else if (!isOpen) {
      // Reset when closing
      setInitialTemplate(null);
      setError(null);
    }
  }, [isOpen, templateId]);

  const loadTemplate = async (id) => {
    setIsLoadingTemplate(true);
    setError(null);
    try {
      const response = await signingService.getTemplate(id);
      const template = response.data;

      // Transform API data to editor format
      setInitialTemplate({
        id: template.id,
        name: template.name,
        folder_name: template.folder_name,
        submitters: template.submitters || [],
        fields: template.fields?.map(f => ({
          uuid: f.uuid,
          name: f.name,
          type: f.type,
          required: f.required !== false,
          roleUuid: null, // Will be matched by role name
          areas: f.areas || [],
        })) || [],
        // Get the document URL for viewing
        documentUrl: template.documents?.[0]?.url || null,
      });
    } catch (err) {
      console.error('Failed to load template:', err);
      setError('Failed to load template. Please try again.');
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const handleSave = useCallback(async (payload, mode, existingTemplateId) => {
    setIsSaving(true);
    setError(null);

    try {
      let response;

      if (mode === 'edit' && existingTemplateId) {
        // Update existing template
        response = await signingService.updateTemplate(existingTemplateId, payload);
      } else {
        // Create new template
        response = await signingService.createTemplateFromPDF(payload);
      }

      if (onSuccess) {
        onSuccess(response.data);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save template:', err);
      let message = 'Failed to save template. Please try again.';

      const detail = err.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors are arrays of {type, loc, msg, input}
          message = detail.map(e => e.msg || JSON.stringify(e)).join('; ');
        } else if (typeof detail === 'object') {
          message = detail.msg || JSON.stringify(detail);
        }
      }

      setError(message);
      throw new Error(message);
    } finally {
      setIsSaving(false);
    }
  }, [onSuccess, onClose]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    onClose();
  }, [isSaving, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isSaving) {
        // Don't close on escape if editor handles it for deselection
        // Only close if nothing is selected (handled by editor)
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isSaving]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-60"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-lg shadow-2xl w-[95vw] h-[90vh] max-w-[1600px] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <FileSignature className="w-6 h-6 text-yellow-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit Template' : 'Create Signing Template'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoadingTemplate ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading template...</p>
              </div>
            </div>
          ) : (
            <TemplateEditor
              initialTemplate={initialTemplate}
              onSave={handleSave}
              onCancel={handleClose}
              isSaving={isSaving}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateEditorModal;
